import { Match, Show, Switch } from "solid-js";
import { ChoiceGroup, Field, FieldSet, RatingScale, TextField } from "../../components/Form.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { answerOptions, isInformational } from "../../api/domain.ts";
import type { AnswerValue, DraftAnswer, Question } from "../../api/domain.ts";
import { selectedOptionSlugs, type SaveState } from "./model.ts";
import styles from "./FillPage.module.scss";

export type QuestionCardProps = {
  question: Question;
  draft?: DraftAnswer;
  saveState: SaveState;
  /** Disabled until the recipient has said which product the answers describe. */
  disabled: boolean;
  onChange: (patch: Partial<DraftAnswer>) => void;
};

const optionChoices = (question: Question) =>
  answerOptions(question).map((option) => ({ id: option.slug, label: option.label }));

const toggleMulti = (current: string[], slug: string, checked: boolean): string[] =>
  checked
    ? [...current.filter((entry) => entry !== slug), slug]
    : current.filter((entry) => entry !== slug);

const boolSelection = (value: AnswerValue | null | undefined): string[] =>
  value?.kind === "bool" ? [value.value ? "yes" : "no"] : [];

const ratingValue = (value: AnswerValue | null | undefined): number | undefined =>
  value?.kind === "rating" ? value.value : undefined;

const textValue = (value: AnswerValue | null | undefined): string =>
  value?.kind === "text" ? value.value : "";

/** One question with its value, comment and explanation; every edit autosaves. */
export const QuestionCard = (props: QuestionCardProps) => {
  const i18n = useI18n();

  const value = () => props.draft?.value;
  const setValue = (next: AnswerValue | null) => props.onChange({ value: next });

  const statusLabel = () => {
    const state = props.saveState.state;
    if (state === "saving") return i18n.t().common.saving;
    if (state === "saved") return i18n.t().common.saved;
    if (state === "failed") return i18n.t().common.saveFailed;
    return "";
  };

  return (
    <article class={styles.card}>
      <div class={styles.cardHead}>
        <h3 class={styles.cardTitle}>{props.question.title}</h3>
        <Show when={props.question.body !== null}>
          <p class={styles.cardBody}>{props.question.body}</p>
        </Show>
        <Show when={isInformational(props.question)}>
          <span class={styles.tag}>{i18n.t().fill.informational}</span>
        </Show>
      </div>

      <FieldSet disabled={props.disabled}>
        <Switch>
          <Match when={props.question.answer_type.kind === "bool"}>
            <Field label={i18n.t().common.answer} hint={i18n.t().fill.dontKnowHint}>
              {() => (
                <ChoiceGroup
                  name={props.question.id}
                  legend={props.question.title}
                  choices={[
                    { id: "yes", label: i18n.t().common.yes },
                    { id: "no", label: i18n.t().common.no },
                  ]}
                  selected={boolSelection(value())}
                  onToggle={(id, checked) =>
                    setValue(checked ? { kind: "bool", value: id === "yes" } : null)
                  }
                />
              )}
            </Field>
          </Match>

          <Match when={props.question.answer_type.kind === "rating"}>
            <Field label={i18n.t().common.answer} hint={i18n.t().fill.ratingHint}>
              {() => (
                <RatingScale
                  name={props.question.id}
                  legend={props.question.title}
                  value={ratingValue(value())}
                  onPick={(rating) => setValue({ kind: "rating", value: rating })}
                />
              )}
            </Field>
          </Match>

          <Match when={props.question.answer_type.kind === "select"}>
            <Field label={i18n.t().common.answer} hint={i18n.t().fill.dontKnowHint}>
              {() => (
                <ChoiceGroup
                  name={props.question.id}
                  legend={props.question.title}
                  choices={optionChoices(props.question)}
                  selected={selectedOptionSlugs(value())}
                  onToggle={(id, checked) =>
                    setValue(checked ? { kind: "select", value: id } : null)
                  }
                />
              )}
            </Field>
          </Match>

          <Match when={props.question.answer_type.kind === "multi_select"}>
            <Field label={i18n.t().common.answer} hint={i18n.t().fill.multiSelectHint}>
              {() => (
                <ChoiceGroup
                  name={props.question.id}
                  legend={props.question.title}
                  multiple
                  choices={optionChoices(props.question)}
                  selected={selectedOptionSlugs(value())}
                  onToggle={(id, checked) =>
                    setValue({
                      kind: "multi_select",
                      value: toggleMulti(selectedOptionSlugs(value()), id, checked),
                    })
                  }
                />
              )}
            </Field>
          </Match>

          <Match when={props.question.answer_type.kind === "text"}>
            <TextField
              label={i18n.t().common.answer}
              hint={i18n.t().fill.textHint}
              multiline
              value={textValue(value())}
              onInput={(text) => setValue({ kind: "text", value: text })}
            />
          </Match>
        </Switch>

        <div class={styles.notes}>
          <TextField
            label={i18n.t().fill.comment}
            hint={i18n.t().fill.commentHint}
            optional
            value={props.draft?.comment ?? ""}
            onInput={(comment) => props.onChange({ comment })}
          />
          <TextField
            label={i18n.t().fill.explanation}
            hint={i18n.t().fill.explanationHint}
            optional
            multiline
            value={props.draft?.explanation ?? ""}
            onInput={(explanation) => props.onChange({ explanation })}
          />
        </div>
      </FieldSet>

      <span
        class={`${styles.status} ${props.saveState.state === "failed" ? styles.statusFailed : ""}`}
        role="status"
        aria-live="polite"
      >
        {statusLabel()}
      </span>
    </article>
  );
};
